import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Paper, Typography, Box, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField,
  Chip, IconButton, Card, CardContent, useMediaQuery,
  Accordion, AccordionSummary, AccordionDetails, Grid, Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add, Edit, Delete, ExpandMore, LocationOn, Assignment
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LocationDialog from '../components/LocationDialog';

const KCManagement = () => {
  const { user } = useAuth();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isAdmin = user?.role === 'admin';
  const [tabValue, setTabValue] = useState(0);
  const [kcs, setKCs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedKC, setSelectedKC] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    planner_group: ''
  });
  const [locationFormData, setLocationFormData] = useState({
    competence_center_id: '',
    plz: '',
    city: '',
    district: '',
    planner_group: ''
  });

  const handleFormFieldChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  useEffect(() => {
    loadKCs();
    loadLocations();
  }, []);

  const loadKCs = async () => {
    try {
      const response = await api.get('/kcs');
      setKCs(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der KCs');
    }
  };

  const loadLocations = async () => {
    try {
      const response = await api.get('/kcs/locations');
      setLocations(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Orte');
    }
  };

  const handleOpenKCDialog = (kc = null) => {
    if (kc) {
      setSelectedKC(kc);
      setFormData({
        code: kc.code,
        name: kc.name,
        description: kc.description || '',
        planner_group: kc.planner_group || ''
      });
    } else {
      setSelectedKC(null);
      setFormData({ code: '', name: '', description: '', planner_group: '' });
    }
    setDialogOpen(true);
  };

  const handleSaveKC = async () => {
    try {
      if (selectedKC) {
        await api.put(`/kcs/${selectedKC.id}`, formData);
        toast.success('KC erfolgreich aktualisiert');
      } else {
        await api.post('/kcs', formData);
        toast.success('KC erfolgreich erstellt');
      }
      setDialogOpen(false);
      loadKCs();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleOpenLocationDialog = (kcId = null, location = null) => {
    if (location) {
      // Bearbeitungsmodus
      setSelectedLocation(location);
      setLocationFormData({
        competence_center_id: location.competence_center_id || kcId,
        plz: location.plz || '',
        city: location.city || '',
        district: location.district || '',
        planner_group: location.planner_group || ''
      });
    } else {
      // Neu-Modus
      setSelectedLocation(null);
      setLocationFormData({
        competence_center_id: kcId || '',
        plz: '',
        city: '',
        district: '',
        planner_group: ''
      });
    }
    setLocationDialogOpen(true);
  };

  const handleSaveLocation = useCallback(async (formData) => {
    try {
      const fullName = formData.plz
        ? `${formData.plz} ${formData.city} - ${formData.district}`
        : `${formData.city} - ${formData.district}`;

      if (selectedLocation) {
        // Update bestehenden Standort
        await api.put(`/kcs/locations/${selectedLocation.id}`, {
          ...formData,
          full_name: fullName
        });
        toast.success('Standort erfolgreich aktualisiert');
      } else {
        // Erstelle neuen Standort
        await api.post('/kcs/locations', {
          ...formData,
          full_name: fullName
        });
        toast.success('Standort erfolgreich erstellt');
      }

      setLocationDialogOpen(false);
      setSelectedLocation(null);
      loadLocations();
    } catch (error) {
      toast.error('Fehler beim Speichern des Standorts');
    }
  }, [selectedLocation]);

  const handleDeleteKC = async (id) => {
    if (window.confirm('Möchten Sie dieses Kompetenz-Center wirklich löschen? Alle zugehörigen Standorte werden ebenfalls gelöscht.')) {
      try {
        await api.delete(`/kcs/${id}`);
        toast.success('Kompetenz-Center erfolgreich gelöscht');
        loadKCs();
        loadLocations();
      } catch (error) {
        toast.error('Fehler beim Löschen des Kompetenz-Centers');
      }
    }
  };

  const handleDeleteLocation = async (id) => {
    if (window.confirm('Möchten Sie diesen Ort wirklich löschen?')) {
      try {
        await api.delete(`/kcs/locations/${id}`);
        toast.success('Ort erfolgreich gelöscht');
        loadLocations();
      } catch (error) {
        toast.error('Fehler beim Löschen');
      }
    }
  };

  const handleUploadMap = async (locationId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const formData = new FormData();
      formData.append('map', file);
      
      try {
        await api.post(`/kcs/locations/${locationId}/map`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Karte erfolgreich hochgeladen');
        loadLocations();
      } catch (error) {
        toast.error('Fehler beim Hochladen der Karte');
      }
    };
    
    input.click();
  };

  const getPlannerGroupColor = (group) => {
    const colors = {
      '31': 'primary',
      '32': 'secondary',
      '33': 'success',
      '34': 'warning',
      '35': 'error',
      '42': 'info',
      '43': 'default',
      '45': 'primary',
      '47': 'secondary',
      '51': 'success',
      '53': 'warning',
      '54P': 'error',
      '55': 'info',
      '55L': 'default'
    };
    return colors[group] || 'default';
  };

  const groupLocationsByKC = () => {
    const grouped = {};
    locations.forEach(location => {
      if (!grouped[location.kc_code]) {
        grouped[location.kc_code] = {
          name: location.kc_name,
          code: location.kc_code,
          locations: []
        };
      }
      grouped[location.kc_code].locations.push(location);
    });
    return grouped;
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
        KC & Standort-Verwaltung
      </Typography>
      
      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sie können die KCs und Standorte einsehen. Zum Bearbeiten benötigen Sie Admin-Rechte.
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Kompetenz-Center" />
          <Tab label="Standorte" />
          <Tab label="Übersicht" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Kompetenz-Center</Typography>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenKCDialog()}
              >
                KC hinzufügen
              </Button>
            )}
          </Box>

          {isMobile ? (
            <Box display="flex" flexDirection="column" gap={1.5}>
              {kcs.map((kc) => (
                <Card key={kc.id}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Chip label={kc.code} color="primary" size="small" />
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>{kc.name}</Typography>
                        </Box>
                        {kc.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{kc.description}</Typography>}
                        <Box display="flex" gap={2}>
                          <Typography variant="caption" color="text.secondary">{kc.location_count || 0} Orte</Typography>
                          <Typography variant="caption" color="text.secondary">{kc.ticket_count || 0} Tickets</Typography>
                        </Box>
                      </Box>
                      {isAdmin && (
                        <Box display="flex">
                          <IconButton size="small" onClick={() => handleOpenKCDialog(kc)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleOpenLocationDialog(kc.id)}><LocationOn fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDeleteKC(kc.id)} color="error"><Delete fontSize="small" /></IconButton>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Beschreibung</TableCell>
                    <TableCell>Orte</TableCell>
                    <TableCell>Tickets</TableCell>
                    <TableCell>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kcs.map((kc) => (
                    <TableRow key={kc.id}>
                      <TableCell><Chip label={kc.code} color="primary" size="small" /></TableCell>
                      <TableCell>{kc.name}</TableCell>
                      <TableCell>{kc.description}</TableCell>
                      <TableCell>{kc.location_count || 0}</TableCell>
                      <TableCell>{kc.ticket_count || 0}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {isAdmin && (
                          <>
                            <IconButton size="small" onClick={() => handleOpenKCDialog(kc)} title="KC bearbeiten"><Edit /></IconButton>
                            <IconButton size="small" onClick={() => handleOpenLocationDialog(kc.id)} title="Ort hinzufügen"><LocationOn /></IconButton>
                            <IconButton size="small" onClick={() => handleDeleteKC(kc.id)} title="KC löschen" color="error"><Delete /></IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Standorte</Typography>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenLocationDialog()}
              >
                Standort hinzufügen
              </Button>
            )}
          </Box>

          {Object.entries(groupLocationsByKC()).map(([kcCode, kcData]) => (
            <Accordion key={kcCode} defaultExpanded={kcCode === 'KC-AMP'}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">
                  {kcData.name} ({kcData.locations.length} Orte)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
                {isMobile ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    {kcData.locations.map((location) => (
                      <Card key={location.id} variant="outlined">
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1} minWidth={0}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {location.plz && `${location.plz} `}{location.city}
                              </Typography>
                              {location.district && (
                                <Typography variant="caption" color="text.secondary">{location.district}</Typography>
                              )}
                              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                <Chip label={`Gr. ${location.planner_group}`} color={getPlannerGroupColor(location.planner_group)} size="small" />
                                {location.map_image_path && (
                                  <Chip label="Karte" color="success" size="small" onClick={() => window.open(`${location.map_image_path}`, '_blank')} sx={{ cursor: 'pointer' }} />
                                )}
                              </Box>
                            </Box>
                            {isAdmin && (
                              <Box display="flex" flexDirection="column">
                                <Box display="flex">
                                  <IconButton size="small" onClick={() => handleOpenLocationDialog(location.competence_center_id, location)} color="primary"><Edit fontSize="small" /></IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteLocation(location.id)} color="error"><Delete fontSize="small" /></IconButton>
                                </Box>
                                {!location.map_image_path && (
                                  <Button size="small" variant="text" onClick={() => handleUploadMap(location.id)} sx={{ fontSize: '0.7rem' }}>
                                    + Karte
                                  </Button>
                                )}
                                {location.map_image_path && (
                                  <Button size="small" variant="text" onClick={() => handleUploadMap(location.id)} sx={{ fontSize: '0.7rem' }}>
                                    Karte ändern
                                  </Button>
                                )}
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>PLZ</TableCell>
                          <TableCell>Stadt</TableCell>
                          <TableCell>Ortsteil</TableCell>
                          <TableCell>Planergruppe</TableCell>
                          <TableCell>Karte</TableCell>
                          <TableCell>Aktionen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {kcData.locations.map((location) => (
                          <TableRow key={location.id}>
                            <TableCell>{location.plz || '-'}</TableCell>
                            <TableCell>{location.city}</TableCell>
                            <TableCell>{location.district || '-'}</TableCell>
                            <TableCell>
                              <Chip label={location.planner_group} color={getPlannerGroupColor(location.planner_group)} size="small" />
                            </TableCell>
                            <TableCell>
                              {location.map_image_path ? (
                                <Box display="flex" gap={1} alignItems="center">
                                  <Chip label="Karte vorhanden" color="success" size="small" onClick={() => window.open(`${location.map_image_path}`, '_blank')} style={{ cursor: 'pointer' }} />
                                  {isAdmin && <Button size="small" variant="outlined" onClick={() => handleUploadMap(location.id)}>Ändern</Button>}
                                </Box>
                              ) : (
                                isAdmin && <Button size="small" variant="outlined" onClick={() => handleUploadMap(location.id)}>Karte hochladen</Button>
                              )}
                            </TableCell>
                            <TableCell>
                              {isAdmin && (
                                <>
                                  <IconButton size="small" onClick={() => handleOpenLocationDialog(location.competence_center_id, location)} title="Standort bearbeiten" color="primary"><Edit /></IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteLocation(location.id)} title="Standort löschen" color="error"><Delete /></IconButton>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Übersicht Planergruppen
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(
              locations.reduce((acc, loc) => {
                if (!acc[loc.planner_group]) {
                  acc[loc.planner_group] = [];
                }
                acc[loc.planner_group].push(loc);
                return acc;
              }, {})
            ).map(([group, locs]) => (
              <Grid item xs={12} sm={6} md={4} key={group}>
                <Paper sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">
                      Gruppe {group}
                    </Typography>
                    <Chip label={`${locs.length} Orte`} size="small" />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {locs.slice(0, 3).map(l => l.city).join(', ')}
                    {locs.length > 3 && '...'}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* KC Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedKC ? 'KC bearbeiten' : 'Neues KC hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Code (z.B. KC-ABC)"
            value={formData.code}
            onChange={handleFormFieldChange('code')}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={formData.name}
            onChange={handleFormFieldChange('name')}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Beschreibung"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleFormFieldChange('description')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSaveKC} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Dialog - ausgelagert um Re-Renders zu vermeiden */}
      <LocationDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        selectedLocation={selectedLocation}
        initialData={locationFormData}
        kcs={kcs}
        onSave={handleSaveLocation}
      />
    </Container>
  );
};

export default KCManagement;